import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { analyzeTableColumns } from '../utils/api';
import { Form, Select, Input, Button, Upload, Card, Spin, Alert, Row, Col, Typography, Collapse, List, message } from 'antd';
import { UploadOutlined, CopyOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Paragraph } = Typography;

// Database vendors configuration
const DATABASE_VENDORS = [
    { value: 'access', label: 'Microsoft Access' },
    { value: 'ansi', label: 'ANSI SQL' },
    { value: 'athena', label: 'AWS Athena' },
    { value: 'azuresql', label: 'Azure SQL' },
    { value: 'bigquery', label: 'Google BigQuery' },
    { value: 'couchbase', label: 'Couchbase' },
    { value: 'databricks', label: 'Databricks' },
    { value: 'dax', label: 'AWS DAX' },
    { value: 'db2', label: 'IBM DB2' },
    { value: 'exasol', label: 'Exasol' },
    { value: 'firebird', label: 'Firebird' },
    { value: 'gaussdb', label: 'GaussDB' },
    { value: 'generic', label: 'Generic SQL' },
    { value: 'greenplum', label: 'Greenplum' },
    { value: 'hana', label: 'SAP HANA' },
    { value: 'hive', label: 'Apache Hive' },
    { value: 'impala', label: 'Apache Impala' },
    { value: 'informix', label: 'IBM Informix' },
    { value: 'mdx', label: 'MDX' },
    { value: 'mssql', label: 'Microsoft SQL Server' },
    { value: 'mysql', label: 'MySQL' },
    { value: 'netezza', label: 'IBM Netezza' },
    { value: 'odbc', label: 'ODBC' },
    { value: 'openedge', label: 'Progress OpenEdge' },
    { value: 'oracle', label: 'Oracle' },
    { value: 'postgresql', label: 'PostgreSQL' },
    { value: 'presto', label: 'Presto' },
    { value: 'redshift', label: 'Amazon Redshift' },
    { value: 'snowflake', label: 'Snowflake' },
    { value: 'soql', label: 'Salesforce SOQL' },
    { value: 'sparksql', label: 'Apache Spark SQL' },
    { value: 'sybase', label: 'Sybase' },
    { value: 'teradata', label: 'Teradata' },
    { value: 'trino', label: 'Trino' },
    { value: 'vertica', label: 'Vertica' },
];

const defaultSql = `SELECT e.last_name,
       e.department_id,
       d.department_name
FROM   employees e
       LEFT OUTER JOIN department d
         ON ( e.department_id = d.department_id );`;

// Interfaces for typing the API response
interface Column {
    name: string;
    table: string;
}
interface Table {
    name: string;
    type: string;
    columns: Column[];
}
interface AnalysisSuccessData {
    tables: Table[];
}
interface ApiResponse {
    code: number;
    data?: AnalysisSuccessData;
    error?: string;
}

const GetTableColumnPage: React.FC = () => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [analysisDuration, setAnalysisDuration] = useState<number | null>(null);

    const mutation = useMutation<ApiResponse, Error, FormData>({
        mutationFn: analyzeTableColumns,
    });

    const handleCopyToClipboard = () => {
        if (mutation.status === 'success' && mutation.data?.data?.tables) {
            const sortedTables = [...mutation.data.data.tables].sort((a, b) => a.name.localeCompare(b.name));

            let csvContent = 'table,column\n';

            sortedTables.forEach(table => {
                table.columns.forEach(column => {
                    csvContent += `${table.name},${column.name}\n`;
                });
            });

            navigator.clipboard.writeText(csvContent.trim())
                .then(() => {
                    message.success('Results copied to clipboard as CSV!');
                })
                .catch(err => {
                    message.error('Failed to copy to clipboard.');
                    console.error('Failed to copy text: ', err);
                });
        }
    };

    const onFinish = async (values: { dbVendor: string; sqlText: string; }) => {
        setAnalysisDuration(null); // Reset the timer on new analysis
        const formData = new FormData();
        formData.append('db_vendor', values.dbVendor);
        
        if (fileList.length > 0 && fileList[0].originFileObj) {
            formData.append('sql_file', fileList[0].originFileObj);
        } else {
            formData.append('sql_text', values.sqlText);
        }
        
        const startTime = performance.now();
        await mutation.mutateAsync(formData);
        const endTime = performance.now();
        setAnalysisDuration(endTime - startTime);
    };

    const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
        setFileList(newFileList);
        if (newFileList.length > 0) {
            message.info('File selected. The SQL text input is now disabled.');
        }
    };

    const handleSqlTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (e.target.value) {
            setFileList([]);
        }
    };

    return (
        <>
            <Title level={2}>Get Table Column</Title>
            <Paragraph>
                Analyze SQL scripts to identify and list all table columns. 
                You can either paste your SQL code directly or upload an SQL file.
            </Paragraph>
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Card title="SQL Analysis Input">
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            initialValues={{ dbVendor: 'oracle', sqlText: defaultSql }}
                        >
                            <Form.Item
                                name="dbVendor"
                                label="Database Vendor"
                                rules={[{ required: true, message: 'Please select a database vendor!' }]}
                            >
                                <Select showSearch placeholder="Select a database vendor" optionFilterProp="children">
                                    {DATABASE_VENDORS.map(vendor => (
                                        <Option key={vendor.value} value={vendor.value}>
                                            {vendor.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="sqlText"
                                label="SQL Text"
                                rules={[{ 
                                    required: fileList.length === 0, 
                                    message: 'Please enter SQL text or upload a file.' 
                                }]}
                            >
                                <TextArea
                                    rows={10}
                                    onChange={handleSqlTextChange}
                                    disabled={fileList.length > 0}
                                    placeholder="Enter your SQL script here."
                                />
                            </Form.Item>

                            <Form.Item
                                label="Or Upload SQL File"
                            >
                                <Upload
                                    name="sqlFile"
                                    fileList={fileList}
                                    onChange={handleFileChange}
                                    beforeUpload={() => false} // Prevent automatic upload
                                    maxCount={1}
                                    accept=".sql"
                                >
                                    <Button icon={<UploadOutlined />}>
                                        Click to Upload
                                    </Button>
                                </Upload>
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={mutation.status === 'pending'}>
                                    Analyze
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                     <Card 
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <span style={{ marginRight: '8px' }}>Analysis Result</span>
                                    {analysisDuration !== null && (
                                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                            (Analyzed in {analysisDuration.toFixed(2)} ms)
                                        </Typography.Text>
                                    )}
                                </div>
                            </div>
                        }
                        extra={
                            mutation.status === 'success' && mutation.data?.data?.tables && mutation.data.data.tables.length > 0 && (
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={handleCopyToClipboard}
                                >
                                    Copy as CSV
                                </Button>
                            )
                        }
                     >
                        {mutation.status === 'pending' && <Spin tip="Analyzing..." />}
                        {mutation.status === 'error' && (
                            <div>
                                <Alert
                                    message="Error"
                                    description={(mutation.error as Error).message}
                                    type="error"
                                    showIcon
                                />
                                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '6px' }}>
                                    <Typography.Text type="danger" strong>Analysis Failed</Typography.Text>
                                    <br />
                                    <Typography.Text type="secondary">
                                        Error Details: {(mutation.error as Error).message}
                                    </Typography.Text>
                                    <br />
                                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                        Please check your SQL syntax and try again, or select a different database vendor.
                                    </Typography.Text>
                                </div>
                            </div>
                        )}
                        {mutation.status === 'success' && mutation.data?.data && (
                            <div>
                                {mutation.data.data.tables && mutation.data.data.tables.length > 0 ? (
                                    <Collapse defaultActiveKey={['0']}>
                                        {mutation.data.data.tables.map((table: Table, index: number) => (
                                            <Collapse.Panel header={`Table: ${table.name}`} key={String(index)}>
                                                <List
                                                    size="small"
                                                    bordered
                                                    dataSource={table.columns}
                                                    renderItem={(column: Column) => (
                                                        <List.Item>
                                                            {column.name}
                                                        </List.Item>
                                                    )}
                                                />
                                            </Collapse.Panel>
                                        ))}
                                    </Collapse>
                                ) : (
                                    <Paragraph type="secondary">No tables or columns were identified in the provided SQL script.</Paragraph>
                                )}
                            </div>
                        )}
                         {mutation.status === 'idle' && (
                            <Paragraph type="secondary">The analysis results will be displayed here.</Paragraph>
                        )}
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default GetTableColumnPage; 